import { Button, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useOpenState } from '@shiftfinancial/react-hooks';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import AccountInfoHeader from '../../components/AccountInfoHeader';
import { CheckOutSummary } from '../../components/CheckOutSummary';
import checkOutSummarySelectors from '../../components/CheckOutSummary/selectors';
import { Dialog } from '../../components/Dialog';
import PageLayout from '../../components/PageLayout';
import { CheckOut } from '../../types';
import accountSelectionSelectors from '../AccountSelection/selectors';
import actions from '../EquipmentFinanceCheckoutConfirmation/actions';

const useStyles: any = makeStyles((theme: any) => ({
  confirmContainer: {
    borderTop: `1px solid ${theme.palette.grey['300']}`,
    padding: theme.spacing(2),
    '& button': {
      width: '100%',
    },
  },
  confirmContainerHavingMultipleButtons: {
    borderTop: `1px solid ${theme.palette.grey['300']}`,
    padding: theme.spacing(2),
    '& button': {
      width: '97%',
    },
  },
  rightButton: {
    [theme.breakpoints.down('xs')]: {
      marginTop: theme.spacing(1),
    },
    [theme.breakpoints.up('sm')]: {
      float: 'right',
      paddingLeft: theme.spacing(1),
    },
  },
}));

const ShiftTradeCheckoutConfirmation = () => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch<any>();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const selectedAccount = useSelector(accountSelectionSelectors.selectSelectedAccount);
  const accounts = useSelector(accountSelectionSelectors.selectAccounts);
  const checkOut: CheckOut = useSelector(checkOutSummarySelectors.selectCheckOut);
  const [isSubmitting, setSubmitting] = useState(false);
  const hasMutipleAccounts = accounts && accounts.length > 1;
  const showDeclineOption = checkOut && checkOut.buyerGcAccountId && !checkOut.redirectUrl;
  const checkoutDeclineDialogOpenState = useOpenState();

  useEffect(() => {
    if (!selectedAccount || !checkOut) {
      history.push({
        pathname: '/500',
      });
    }
  }, []);

  const handleBack = () => {
    history.push({
      pathname: '/select-account',
    });
  };

  const handlePay = () => {
    closeSnackbar();
    setSubmitting(true);

    if (!selectedAccount) {
      return;
    }

    dispatch(actions.confirmPay(checkOut.id, selectedAccount?.gcAccountId))
      .then((response: any) => {
        const { payload } = response;
        if (payload.response && payload.response.status !== 200) {
          if (payload.response.data && payload.response.data.message) {
            enqueueSnackbar(payload.response.data.message, {
              anchorOrigin: { vertical: 'top', horizontal: 'center' },
              variant: 'error',
              key: 'shift',
              autoHideDuration: 12000,
            });
          } else {
            history.push({
              pathname: '/500',
            });
          }
        } else {
          history.push({
            pathname: '/success',
            state: { redirectUrl: checkOut.redirectUrl, isDecline: false },
          });
        }
      })
      .finally(() => setSubmitting(false));
  };

  const handleDecline = () => {
    closeSnackbar();
    setSubmitting(true);

    dispatch(actions.declineCheckOut(checkOut.id))
      .then((response: any) => {
        const { payload } = response;
        if (payload.response && payload.response.status !== 200) {
          if (payload.response.data && payload.response.data.message) {
            enqueueSnackbar(payload.response.data.message, {
              anchorOrigin: { vertical: 'top', horizontal: 'center' },
              variant: 'error',
              key: 'shift',
              autoHideDuration: 12000,
            });
          } else {
            history.push({
              pathname: '/500',
            });
          }
        } else {
          history.push({
            pathname: '/success',
            state: { isDecline: true },
          });
        }
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <PageLayout pageTitle='Confirm purchase'>
      {selectedAccount && <AccountInfoHeader name={selectedAccount.name} abn={selectedAccount.abn} />}
      <CheckOutSummary />
      <Grid container className={hasMutipleAccounts ? classes.confirmContainerHavingMultipleButtons : classes.confirmContainer}>
        <Dialog
          description='Are you sure you want to decline this purchase?'
          title='Decline purchase'
          onConfirm={handleDecline}
          openState={checkoutDeclineDialogOpenState}
        ></Dialog>
        {hasMutipleAccounts && (
          <Grid item xs={12} sm={6}>
            <Button
              size='large'
              variant='outlined'
              color='primary'
              type='button'
              disabled={isSubmitting && !!selectedAccount}
              data-testid='uia-goback'
              onClick={() => handleBack()}
            >
              Go back
            </Button>
          </Grid>
        )}
        {!hasMutipleAccounts && showDeclineOption && (
          <Grid item xs={12} sm={6}>
            <Button size='large' variant='outlined' color='primary' data-testid='uia-decline' onClick={() => checkoutDeclineDialogOpenState.open()}>
              Decline
            </Button>
          </Grid>
        )}
        <Grid item className={classes.rightButton} xs={12} sm={hasMutipleAccounts || showDeclineOption ? 6 : 12}>
          <Button
            size='large'
            variant='contained'
            color='primary'
            type='button'
            disabled={isSubmitting && !!selectedAccount}
            data-testid='uia-pay'
            className={classes.rightButton}
            onClick={handlePay}
          >
            Pay
          </Button>
        </Grid>
      </Grid>
    </PageLayout>
  );
};

export default ShiftTradeCheckoutConfirmation;
